import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { AnimatePresence,motion } from "framer-motion";
import { 
  Frown,
  Image as ImageIcon,
  Loader2,
  Meh,
  MessageSquare, 
  Send,
  Smile,
  Star, 
  ThumbsUp,
  X} from "lucide-react";
import { useState } from "react";

type FeedbackType = "suggestion" | "bug" | "question" | "other";
type SatisfactionLevel = "satisfied" | "neutral" | "unsatisfied";

interface FeedbackForm {
  type: FeedbackType;
  satisfaction: SatisfactionLevel;
  title: string;
  description: string;
  attachments: File[];
}

const FeedbackPage = () => {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [form, setForm] = useState<FeedbackForm>({
    type: "suggestion",
    satisfaction: "satisfied",
    title: "",
    description: "",
    attachments: [],
  });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length + form.attachments.length > 3) {
      toast({
        title: "Помилка",
        description: "Можна додати максимум 3 файли",
        variant: "destructive",
      });
      return;
    }
    setForm(prev => ({
      ...prev,
      attachments: [...prev.attachments, ...files],
    }));
  };

  const removeAttachment = (index: number) => {
    setForm(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // TODO: Implement API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      toast({
        title: "Дякуємо за відгук!",
        description: "Ваш відгук успішно надіслано",
      });

      setForm({
        type: "suggestion",
        satisfaction: "satisfied",
        title: "",
        description: "",
        attachments: [],
      });
    } catch (error) {
      toast({
        title: "Помилка",
        description: "Не вдалося надіслати відгук. Спробуйте пізніше",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="container mx-auto p-8">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <div className="p-2 bg-primary/10 rounded-full">
            <MessageSquare className="h-8 w-8 text-primary" />
          </div>
          <div>
            <h1 className="text-3xl font-bold">Зворотний зв'язок</h1>
            <p className="text-muted-foreground">Поділіться своїми враженнями та пропозиціями</p>
          </div>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-3">
        <Card className="md:col-span-2">
          <CardHeader>
            <CardTitle>Надіслати відгук</CardTitle>
            <CardDescription>
              Розкажіть нам про свій досвід використання системи
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label>Тип відгуку</Label>
                <RadioGroup
                  value={form.type}
                  onValueChange={(value: FeedbackType) =>
                    setForm(prev => ({ ...prev, type: value }))
                  }
                  className="grid grid-cols-2 gap-4"
                >
                  <Label
                    htmlFor="suggestion"
                    className={cn(
                      "flex items-center justify-center gap-2 rounded-lg border p-4 cursor-pointer hover:bg-accent",
                      form.type === "suggestion" && "border-primary bg-primary/5"
                    )}
                  >
                    <RadioGroupItem value="suggestion" id="suggestion" className="sr-only" />
                    <Star className="h-5 w-5" />
                    <span>Пропозиція</span>
                  </Label>
                  <Label
                    htmlFor="bug"
                    className={cn(
                      "flex items-center justify-center gap-2 rounded-lg border p-4 cursor-pointer hover:bg-accent",
                      form.type === "bug" && "border-primary bg-primary/5"
                    )}
                  >
                    <RadioGroupItem value="bug" id="bug" className="sr-only" />
                    <Frown className="h-5 w-5" />
                    <span>Помилка</span>
                  </Label>
                  <Label
                    htmlFor="question"
                    className={cn(
                      "flex items-center justify-center gap-2 rounded-lg border p-4 cursor-pointer hover:bg-accent",
                      form.type === "question" && "border-primary bg-primary/5"
                    )}
                  >
                    <RadioGroupItem value="question" id="question" className="sr-only" />
                    <MessageSquare className="h-5 w-5" />
                    <span>Запитання</span>
                  </Label>
                  <Label
                    htmlFor="other"
                    className={cn(
                      "flex items-center justify-center gap-2 rounded-lg border p-4 cursor-pointer hover:bg-accent",
                      form.type === "other" && "border-primary bg-primary/5"
                    )}
                  >
                    <RadioGroupItem value="other" id="other" className="sr-only" />
                    <ThumbsUp className="h-5 w-5" />
                    <span>Інше</span>
                  </Label>
                </RadioGroup>
              </div>

              <div className="space-y-2">
                <Label>Рівень задоволеності</Label>
                <RadioGroup
                  value={form.satisfaction}
                  onValueChange={(value: SatisfactionLevel) =>
                    setForm(prev => ({ ...prev, satisfaction: value }))
                  }
                  className="flex justify-center gap-8"
                >
                  <Label
                    htmlFor="satisfied"
                    className={cn(
                      "flex flex-col items-center gap-2 cursor-pointer",
                      form.satisfaction === "satisfied" && "text-green-500"
                    )}
                  >
                    <RadioGroupItem value="satisfied" id="satisfied" className="sr-only" />
                    <Smile className="h-8 w-8" />
                    <span>Задоволений</span>
                  </Label>
                  <Label
                    htmlFor="neutral"
                    className={cn(
                      "flex flex-col items-center gap-2 cursor-pointer",
                      form.satisfaction === "neutral" && "text-yellow-500"
                    )}
                  >
                    <RadioGroupItem value="neutral" id="neutral" className="sr-only" />
                    <Meh className="h-8 w-8" />
                    <span>Нейтрально</span>
                  </Label>
                  <Label
                    htmlFor="unsatisfied"
                    className={cn(
                      "flex flex-col items-center gap-2 cursor-pointer",
                      form.satisfaction === "unsatisfied" && "text-red-500"
                    )}
                  >
                    <RadioGroupItem value="unsatisfied" id="unsatisfied" className="sr-only" />
                    <Frown className="h-8 w-8" />
                    <span>Незадоволений</span>
                  </Label>
                </RadioGroup>
              </div>

              <div className="space-y-2">
                <Label htmlFor="title">Тема</Label>
                <Input
                  id="title"
                  placeholder="Коротко опишіть суть відгуку"
                  value={form.title}
                  onChange={(e) => setForm(prev => ({ ...prev, title: e.target.value }))}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Опис</Label>
                <Textarea
                  id="description"
                  placeholder="Детально опишіть свій відгук..."
                  value={form.description}
                  onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
                  className="min-h-[150px]"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label>Вкладення</Label>
                <div className="flex flex-wrap gap-2">
                  <AnimatePresence>
                    {form.attachments.map((file, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.8 }}
                        className="flex items-center gap-2 rounded-lg border p-2"
                      >
                        <ImageIcon className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm">{file.name}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-4 w-4 p-0"
                          onClick={() => removeAttachment(index)}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                  {form.attachments.length < 3 && (
                    <Label
                      htmlFor="file"
                      className="flex items-center gap-2 rounded-lg border p-2 cursor-pointer hover:bg-accent"
                    >
                      <ImageIcon className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm">Додати файл</span>
                      <Input
                        id="file"
                        type="file"
                        className="hidden"
                        onChange={handleFileChange}
                        accept="image/*,.pdf,.doc,.docx"
                      />
                    </Label>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  Максимум 3 файли (зображення, PDF, DOC)
                </p>
              </div>

              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Надсилання...
                  </>
                ) : (
                  <>
                    <Send className="mr-2 h-4 w-4" />
                    Надіслати відгук
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Що буде далі?</CardTitle>
              <CardDescription>
                Процес обробки вашого відгуку
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-start gap-4">
                <div className="p-2 bg-primary/10 rounded-full">
                  <MessageSquare className="h-4 w-4 text-primary" />
                </div>
                <div>
                  <h3 className="font-medium">Отримання</h3>
                  <p className="text-sm text-muted-foreground">
                    Ми отримаємо ваш відгук та почнемо його обробку
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="p-2 bg-primary/10 rounded-full">
                  <Star className="h-4 w-4 text-primary" />
                </div>
                <div>
                  <h3 className="font-medium">Аналіз</h3>
                  <p className="text-sm text-muted-foreground">
                    Наша команда проаналізує ваш відгук та визначить пріоритет
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="p-2 bg-primary/10 rounded-full">
                  <Send className="h-4 w-4 text-primary" />
                </div>
                <div>
                  <h3 className="font-medium">Відповідь</h3>
                  <p className="text-sm text-muted-foreground">
                    Ми зв'яжемося з вами, якщо потрібна додаткова інформація
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Корисні поради</CardTitle>
              <CardDescription>
                Як написати ефективний відгук
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-start gap-4">
                <div className="p-2 bg-primary/10 rounded-full">
                  <ThumbsUp className="h-4 w-4 text-primary" />
                </div>
                <div>
                  <h3 className="font-medium">Будьте конкретними</h3>
                  <p className="text-sm text-muted-foreground">
                    Опишіть конкретну ситуацію або проблему
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="p-2 bg-primary/10 rounded-full">
                  <ImageIcon className="h-4 w-4 text-primary" />
                </div>
                <div>
                  <h3 className="font-medium">Додайте скріншоти</h3>
                  <p className="text-sm text-muted-foreground">
                    Якщо можливо, додайте зображення для ілюстрації
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="p-2 bg-primary/10 rounded-full">
                  <MessageSquare className="h-4 w-4 text-primary" />
                </div>
                <div>
                  <h3 className="font-medium">Пропонуйте рішення</h3>
                  <p className="text-sm text-muted-foreground">
                    Якщо у вас є ідеї щодо покращення, поділіться ними
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default FeedbackPage; 